import { useEffect, useRef } from 'react'

/**
 * Bach — Fugue in G minor, BWV 578 ("Little Fugue")
 * First 5 measures, treble clef — scrolling SVG notation.
 *
 * Props:
 *  - color: note/stem/beam fill color (default: currentColor)
 *  - staffColor: staff line color (default: color at 30% opacity)
 *  - speed: pixels per second (default: 50)
 *  - height: container height in px (default: 48)
 *  - className: additional classes on the wrapper
 *  - showBranding: show "FUGUE LAB" text and Bach attribution (default: false)
 */

interface ScrollingNotationProps {
  color?: string
  staffColor?: string
  speed?: number
  height?: number
  className?: string
  showBranding?: boolean
}

export function ScrollingNotation({
  color = 'currentColor',
  staffColor,
  speed = 50,
  height = 48,
  className = '',
  showBranding = false,
}: ScrollingNotationProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)

  const resolvedStaffColor = staffColor ?? color

  // The notation SVG content (no branding variant)
  const notationStartX = showBranding ? 150 : 0
  const svgWidth = showBranding ? 1240 : 1000
  // Staff lines span from clef to final barline
  const staffStartX = showBranding ? 145 : 0
  const staffEndX = showBranding ? 1078 : 928

  useEffect(() => {
    const scroll = scrollRef.current
    const svg = svgRef.current
    if (!scroll || !svg) return

    // Clone enough copies to fill viewport + buffer
    const w = svg.getBoundingClientRect().width
    if (w === 0) return
    const copies = Math.ceil(window.innerWidth / w) + 2
    for (let i = 0; i < copies; i++) {
      scroll.appendChild(svg.cloneNode(true))
    }

    // Pixel-perfect scroll animation
    const duration = w / speed
    scroll.style.setProperty('--scroll-w', `-${w}px`)
    scroll.style.animationDuration = `${duration}s`
  }, [speed])

  return (
    <div className={`relative overflow-hidden ${className}`} style={{ height }}>
      {/* Edge fades */}
      <div
        className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10"
        style={{ background: 'linear-gradient(90deg, var(--fade-color, transparent), transparent)' }}
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10"
        style={{ background: 'linear-gradient(270deg, var(--fade-color, transparent), transparent)' }}
      />

      <div
        ref={scrollRef}
        className="flex gap-0"
        style={{
          animation: `scrollNotation 20s linear infinite`,
          whiteSpace: 'nowrap',
          fontSize: 0,
          lineHeight: 0,
        }}
      >
        <svg
          ref={svgRef}
          height={height}
          viewBox={`0 0 ${svgWidth} 76`}
          preserveAspectRatio="xMinYMid meet"
          xmlns="http://www.w3.org/2000/svg"
          className="m-0 block flex-shrink-0 p-0"
          style={{ display: 'inline-block', verticalAlign: 'top' }}
        >
          {/* Staff lines */}
          <line
            x1={staffStartX}
            y1="22"
            x2={staffEndX}
            y2="22"
            stroke={resolvedStaffColor}
            strokeWidth="0.6"
            strokeOpacity="0.3"
          />
          <line
            x1={staffStartX}
            y1="31"
            x2={staffEndX}
            y2="31"
            stroke={resolvedStaffColor}
            strokeWidth="0.6"
            strokeOpacity="0.3"
          />
          <line
            x1={staffStartX}
            y1="40"
            x2={staffEndX}
            y2="40"
            stroke={resolvedStaffColor}
            strokeWidth="0.6"
            strokeOpacity="0.3"
          />
          <line
            x1={staffStartX}
            y1="49"
            x2={staffEndX}
            y2="49"
            stroke={resolvedStaffColor}
            strokeWidth="0.6"
            strokeOpacity="0.3"
          />
          <line
            x1={staffStartX}
            y1="58"
            x2={staffEndX}
            y2="58"
            stroke={resolvedStaffColor}
            strokeWidth="0.6"
            strokeOpacity="0.3"
          />

          {/* Branding (optional) */}
          {showBranding && (
            <>
              <text
                x="10"
                y="38"
                fontSize="14"
                fill={color}
                fontWeight="600"
                letterSpacing="1"
                fontFamily="'Inter','Helvetica Neue',sans-serif"
              >
                FUGUE LAB
              </text>
              <text
                x="10"
                y="54"
                fontSize="10"
                fill={color}
                fontStyle="italic"
                opacity="0.7"
                fontFamily="'Cormorant Garamond',Georgia,serif"
              >
                Great music deserves to be heard
              </text>
              <text
                x="1090"
                y="38"
                fontSize="10"
                fill={color}
                fontStyle="italic"
                opacity="0.7"
                fontFamily="'Cormorant Garamond',Georgia,serif"
              >
                J.S. Bach
              </text>
              <text
                x="1090"
                y="52"
                fontSize="9"
                fill={color}
                opacity="0.5"
                fontFamily="'Inter','Helvetica Neue',sans-serif"
              >
                Fugue in G minor, BWV 578
              </text>
            </>
          )}

          {/* All music notation */}
          <g transform={`translate(${notationStartX},0)`}>
            {/* Treble Clef */}
            <text
              x="-2"
              y="65"
              fontSize="62"
              fill={color}
              fontFamily="'Apple Symbols','Noto Music','Segoe UI Symbol',serif"
            >
              &#x1D11E;
            </text>

            {/* Key Signature: Bb and Eb */}
            <text x="32" y="44" fontSize="20" fill={color} fontFamily="'Apple Symbols','Noto Music',serif">
              &#x266D;
            </text>
            <text x="42" y="30" fontSize="20" fill={color} fontFamily="'Apple Symbols','Noto Music',serif">
              &#x266D;
            </text>

            {/* Time Signature: Common Time */}
            <text
              x="52"
              y="47"
              fontSize="22"
              fontWeight="bold"
              fill={color}
              fontFamily="'Times New Roman',Georgia,serif"
            >
              C
            </text>

            {/* ══ MEASURE 1 ══ */}
            {/* G4 quarter */}
            <ellipse cx="78" cy="49" rx="4.5" ry="3" transform="rotate(-20 78 49)" fill={color} />
            <line x1="83" y1="49" x2="83" y2="22" stroke={color} strokeWidth="1.1" />
            {/* D5 quarter (stem down) */}
            <ellipse cx="100" cy="31" rx="4.5" ry="3" transform="rotate(-20 100 31)" fill={color} />
            <line x1="95" y1="31" x2="95" y2="58" stroke={color} strokeWidth="1.1" />
            {/* Bb4 dotted quarter */}
            <ellipse cx="120" cy="40" rx="4.5" ry="3" transform="rotate(-20 120 40)" fill={color} />
            <line x1="125" y1="40" x2="125" y2="13" stroke={color} strokeWidth="1.1" />
            <circle cx="131" cy="35.5" r="1.3" fill={color} />
            {/* A4 eighth (flagged) */}
            <ellipse cx="147" cy="44.5" rx="4.5" ry="3" transform="rotate(-20 147 44.5)" fill={color} />
            <line x1="152" y1="44.5" x2="152" y2="17" stroke={color} strokeWidth="1.1" />
            <path
              d="M 152 17 C 156 20,158 25,154 30"
              fill="none"
              stroke={color}
              strokeWidth="1.2"
              strokeLinecap="round"
            />
            {/* Barline */}
            <line x1="164" y1="22" x2="164" y2="58" stroke={color} strokeWidth="0.8" />

            {/* M2–M5 shifted 16px */}
            <g transform="translate(16,0)">
              {/* ══ MEASURE 2 ══ */}
              {/* Beam 1: G4 Bb4 A4 G4 */}
              <ellipse cx="160" cy="49" rx="4.5" ry="3" transform="rotate(-20 160 49)" fill={color} />
              <line x1="165" y1="49" x2="165" y2="14" stroke={color} strokeWidth="1.1" />
              <ellipse cx="176" cy="40" rx="4.5" ry="3" transform="rotate(-20 176 40)" fill={color} />
              <line x1="181" y1="40" x2="181" y2="14" stroke={color} strokeWidth="1.1" />
              <ellipse cx="192" cy="44.5" rx="4.5" ry="3" transform="rotate(-20 192 44.5)" fill={color} />
              <line x1="197" y1="44.5" x2="197" y2="14" stroke={color} strokeWidth="1.1" />
              <ellipse cx="208" cy="49" rx="4.5" ry="3" transform="rotate(-20 208 49)" fill={color} />
              <line x1="213" y1="49" x2="213" y2="14" stroke={color} strokeWidth="1.1" />
              <polygon points="165,14 213,14 213,17 165,17" fill={color} />

              {/* ♯ F#4 A4 */}
              <text x="218" y="57" fontSize="16" fill={color} fontFamily="'Apple Symbols','Noto Music',serif">
                &#x266F;
              </text>
              <ellipse cx="233" cy="53.5" rx="4.5" ry="3" transform="rotate(-20 233 53.5)" fill={color} />
              <line x1="238" y1="53.5" x2="238" y2="26" stroke={color} strokeWidth="1.1" />
              <ellipse cx="246" cy="44.5" rx="4.5" ry="3" transform="rotate(-20 246 44.5)" fill={color} />
              <line x1="251" y1="44.5" x2="251" y2="17" stroke={color} strokeWidth="1.1" />
              <polygon points="238,26 251,17 251,20 238,29" fill={color} />

              {/* D4 quarter */}
              <ellipse cx="264" cy="62.5" rx="4.5" ry="3" transform="rotate(-20 264 62.5)" fill={color} />
              <line x1="269" y1="62.5" x2="269" y2="35" stroke={color} strokeWidth="1.1" />
              <line x1="280" y1="22" x2="280" y2="58" stroke={color} strokeWidth="0.8" />

              {/* ══ MEASURE 3 ══ */}
              {/* Beam 1: G4 D4 A4 D4 */}
              <ellipse cx="292" cy="49" rx="4.5" ry="3" transform="rotate(-20 292 49)" fill={color} />
              <line x1="297" y1="49" x2="297" y2="16" stroke={color} strokeWidth="1.1" />
              <ellipse cx="308" cy="62.5" rx="4.5" ry="3" transform="rotate(-20 308 62.5)" fill={color} />
              <line x1="313" y1="62.5" x2="313" y2="16" stroke={color} strokeWidth="1.1" />
              <ellipse cx="324" cy="44.5" rx="4.5" ry="3" transform="rotate(-20 324 44.5)" fill={color} />
              <line x1="329" y1="44.5" x2="329" y2="16" stroke={color} strokeWidth="1.1" />
              <ellipse cx="340" cy="62.5" rx="4.5" ry="3" transform="rotate(-20 340 62.5)" fill={color} />
              <line x1="345" y1="62.5" x2="345" y2="16" stroke={color} strokeWidth="1.1" />
              <polygon points="297,16 345,16 345,19 297,19" fill={color} />

              {/* Beam 2: Bb4(e) A4(s) G4(s) */}
              <ellipse cx="358" cy="40" rx="4.5" ry="3" transform="rotate(-20 358 40)" fill={color} />
              <line x1="363" y1="40" x2="363" y2="12" stroke={color} strokeWidth="1.1" />
              <ellipse cx="372" cy="44.5" rx="4.5" ry="3" transform="rotate(-20 372 44.5)" fill={color} />
              <line x1="377" y1="44.5" x2="377" y2="12" stroke={color} strokeWidth="1.1" />
              <ellipse cx="386" cy="49" rx="4.5" ry="3" transform="rotate(-20 386 49)" fill={color} />
              <line x1="391" y1="49" x2="391" y2="12" stroke={color} strokeWidth="1.1" />
              <polygon points="363,12 391,12 391,15 363,15" fill={color} />
              <polygon points="377,15 391,15 391,18 377,18" fill={color} />

              {/* Beam 3: A4 D4 */}
              <ellipse cx="402" cy="44.5" rx="4.5" ry="3" transform="rotate(-20 402 44.5)" fill={color} />
              <line x1="407" y1="44.5" x2="407" y2="17" stroke={color} strokeWidth="1.1" />
              <ellipse cx="418" cy="62.5" rx="4.5" ry="3" transform="rotate(-20 418 62.5)" fill={color} />
              <line x1="423" y1="62.5" x2="423" y2="35" stroke={color} strokeWidth="1.1" />
              <polygon points="407,17 423,35 423,38 407,20" fill={color} />
              <line x1="434" y1="22" x2="434" y2="58" stroke={color} strokeWidth="0.8" />

              {/* ══ MEASURE 4 ══ */}
              {/* Beam 1: G4(e) D4(s) G4(s) */}
              <ellipse cx="448" cy="49" rx="4.5" ry="3" transform="rotate(-20 448 49)" fill={color} />
              <line x1="453" y1="49" x2="453" y2="22" stroke={color} strokeWidth="1.1" />
              <ellipse cx="462" cy="62.5" rx="4.5" ry="3" transform="rotate(-20 462 62.5)" fill={color} />
              <line x1="467" y1="62.5" x2="467" y2="22" stroke={color} strokeWidth="1.1" />
              <ellipse cx="476" cy="49" rx="4.5" ry="3" transform="rotate(-20 476 49)" fill={color} />
              <line x1="481" y1="49" x2="481" y2="22" stroke={color} strokeWidth="1.1" />
              <polygon points="453,22 481,22 481,25 453,25" fill={color} />
              <polygon points="467,25 481,25 481,28 467,28" fill={color} />

              {/* Beam 2: A4(e) D4(s) A4(s) */}
              <ellipse cx="496" cy="44.5" rx="4.5" ry="3" transform="rotate(-20 496 44.5)" fill={color} />
              <line x1="501" y1="44.5" x2="501" y2="17" stroke={color} strokeWidth="1.1" />
              <ellipse cx="510" cy="62.5" rx="4.5" ry="3" transform="rotate(-20 510 62.5)" fill={color} />
              <line x1="515" y1="62.5" x2="515" y2="17" stroke={color} strokeWidth="1.1" />
              <ellipse cx="524" cy="44.5" rx="4.5" ry="3" transform="rotate(-20 524 44.5)" fill={color} />
              <line x1="529" y1="44.5" x2="529" y2="17" stroke={color} strokeWidth="1.1" />
              <polygon points="501,17 529,17 529,20 501,20" fill={color} />
              <polygon points="515,20 529,20 529,23 515,23" fill={color} />

              {/* Beam 3: Bb4(e) A4(s) G4(s) */}
              <ellipse cx="542" cy="40" rx="4.5" ry="3" transform="rotate(-20 542 40)" fill={color} />
              <line x1="547" y1="40" x2="547" y2="13" stroke={color} strokeWidth="1.1" />
              <ellipse cx="556" cy="44.5" rx="4.5" ry="3" transform="rotate(-20 556 44.5)" fill={color} />
              <line x1="561" y1="44.5" x2="561" y2="18" stroke={color} strokeWidth="1.1" />
              <ellipse cx="570" cy="49" rx="4.5" ry="3" transform="rotate(-20 570 49)" fill={color} />
              <line x1="575" y1="49" x2="575" y2="22" stroke={color} strokeWidth="1.1" />
              <polygon points="547,13 575,22 575,25 547,16" fill={color} />
              <polygon points="561,18 575,25 575,28 561,21" fill={color} />

              {/* Beam 4: A4(s) D4(s) D5(s) C5(s) */}
              <ellipse cx="588" cy="44.5" rx="4.5" ry="3" transform="rotate(-20 588 44.5)" fill={color} />
              <line x1="593" y1="44.5" x2="593" y2="5" stroke={color} strokeWidth="1.1" />
              <ellipse cx="602" cy="62.5" rx="4.5" ry="3" transform="rotate(-20 602 62.5)" fill={color} />
              <line x1="607" y1="62.5" x2="607" y2="5" stroke={color} strokeWidth="1.1" />
              <ellipse cx="616" cy="31" rx="4.5" ry="3" transform="rotate(-20 616 31)" fill={color} />
              <line x1="621" y1="31" x2="621" y2="5" stroke={color} strokeWidth="1.1" />
              <ellipse cx="630" cy="35.5" rx="4.5" ry="3" transform="rotate(-20 630 35.5)" fill={color} />
              <line x1="635" y1="35.5" x2="635" y2="5" stroke={color} strokeWidth="1.1" />
              <polygon points="593,5 635,5 635,8 593,8" fill={color} />
              <polygon points="593,8 635,8 635,11 593,11" fill={color} />
              <line x1="648" y1="22" x2="648" y2="58" stroke={color} strokeWidth="0.8" />

              {/* ══ MEASURE 5 ══ */}
              {/* Beam 1: Bb4 A4 G4 Bb4 */}
              <ellipse cx="662" cy="40" rx="4.5" ry="3" transform="rotate(-20 662 40)" fill={color} />
              <line x1="667" y1="40" x2="667" y2="12" stroke={color} strokeWidth="1.1" />
              <ellipse cx="676" cy="44.5" rx="4.5" ry="3" transform="rotate(-20 676 44.5)" fill={color} />
              <line x1="681" y1="44.5" x2="681" y2="12" stroke={color} strokeWidth="1.1" />
              <ellipse cx="690" cy="49" rx="4.5" ry="3" transform="rotate(-20 690 49)" fill={color} />
              <line x1="695" y1="49" x2="695" y2="12" stroke={color} strokeWidth="1.1" />
              <ellipse cx="704" cy="40" rx="4.5" ry="3" transform="rotate(-20 704 40)" fill={color} />
              <line x1="709" y1="40" x2="709" y2="12" stroke={color} strokeWidth="1.1" />
              <polygon points="667,12 709,12 709,15 667,15" fill={color} />
              <polygon points="667,15 709,15 709,18 667,18" fill={color} />

              {/* Beam 2: A4 G4 F#4 A4 */}
              <ellipse cx="720" cy="44.5" rx="4.5" ry="3" transform="rotate(-20 720 44.5)" fill={color} />
              <line x1="725" y1="44.5" x2="725" y2="16" stroke={color} strokeWidth="1.1" />
              <ellipse cx="734" cy="49" rx="4.5" ry="3" transform="rotate(-20 734 49)" fill={color} />
              <line x1="739" y1="49" x2="739" y2="16" stroke={color} strokeWidth="1.1" />
              <text x="742" y="57" fontSize="15" fill={color} fontFamily="'Apple Symbols','Noto Music',serif">
                &#x266F;
              </text>
              <ellipse cx="757" cy="53.5" rx="4.5" ry="3" transform="rotate(-20 757 53.5)" fill={color} />
              <line x1="762" y1="53.5" x2="762" y2="16" stroke={color} strokeWidth="1.1" />
              <ellipse cx="768" cy="44.5" rx="4.5" ry="3" transform="rotate(-20 768 44.5)" fill={color} />
              <line x1="773" y1="44.5" x2="773" y2="16" stroke={color} strokeWidth="1.1" />
              <polygon points="725,16 773,16 773,19 725,19" fill={color} />
              <polygon points="725,19 773,19 773,22 725,22" fill={color} />

              {/* Beam 3: G4 D4 G4 A4 */}
              <ellipse cx="786" cy="49" rx="4.5" ry="3" transform="rotate(-20 786 49)" fill={color} />
              <line x1="791" y1="49" x2="791" y2="16" stroke={color} strokeWidth="1.1" />
              <ellipse cx="800" cy="62.5" rx="4.5" ry="3" transform="rotate(-20 800 62.5)" fill={color} />
              <line x1="805" y1="62.5" x2="805" y2="16" stroke={color} strokeWidth="1.1" />
              <ellipse cx="814" cy="49" rx="4.5" ry="3" transform="rotate(-20 814 49)" fill={color} />
              <line x1="819" y1="49" x2="819" y2="16" stroke={color} strokeWidth="1.1" />
              <ellipse cx="828" cy="44.5" rx="4.5" ry="3" transform="rotate(-20 828 44.5)" fill={color} />
              <line x1="833" y1="44.5" x2="833" y2="16" stroke={color} strokeWidth="1.1" />
              <polygon points="791,16 833,16 833,19 791,19" fill={color} />
              <polygon points="791,19 833,19 833,22 791,22" fill={color} />

              {/* Beam 4: Bb4 C5 D5 E♮5 */}
              <ellipse cx="846" cy="40" rx="4.5" ry="3" transform="rotate(-20 846 40)" fill={color} />
              <line x1="851" y1="40" x2="851" y2="2" stroke={color} strokeWidth="1.1" />
              <ellipse cx="860" cy="35.5" rx="4.5" ry="3" transform="rotate(-20 860 35.5)" fill={color} />
              <line x1="865" y1="35.5" x2="865" y2="2" stroke={color} strokeWidth="1.1" />
              <ellipse cx="874" cy="31" rx="4.5" ry="3" transform="rotate(-20 874 31)" fill={color} />
              <line x1="879" y1="31" x2="879" y2="2" stroke={color} strokeWidth="1.1" />
              <text x="882" y="30" fontSize="15" fill={color} fontFamily="'Apple Symbols','Noto Music',serif">
                &#x266E;
              </text>
              <ellipse cx="897" cy="26.5" rx="4.5" ry="3" transform="rotate(-20 897 26.5)" fill={color} />
              <line x1="902" y1="26.5" x2="902" y2="2" stroke={color} strokeWidth="1.1" />
              <polygon points="851,2 902,2 902,5 851,5" fill={color} />
              <polygon points="851,5 902,5 902,8 851,8" fill={color} />

              {/* Final double barline */}
              <line x1="908" y1="22" x2="908" y2="58" stroke={color} strokeWidth="0.8" />
              <line x1="911" y1="22" x2="911" y2="58" stroke={color} strokeWidth="2.2" />
            </g>
          </g>
        </svg>
      </div>

      <style>{`
        @keyframes scrollNotation {
          0% { transform: translateX(0); }
          100% { transform: translateX(var(--scroll-w, -50%)); }
        }
      `}</style>
    </div>
  )
}
